// UserDataContext.js - Centralized user data management
import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { 
  getUserData, 
  claimMinedSecondaryCoin, 
  getDailyLoginInfo, 
  claimDailyLoginReward, 
  claimSponsorReward,
  updateMiningProgress
} from '../services/appwrite';
import { APP_CONFIG } from '../config/app';

const UserDataContext = createContext();

export const UserDataProvider = ({ children, initialData }) => { // Accept initialData prop
  const [userData, setUserData] = useState(null);
  const [sponsors, setSponsors] = useState([]); // New state for sponsors
  const [referralStats, setReferralStats] = useState(null); // New state for referral stats
  const [isLoading, setIsLoading] = useState(true); // Start with loading true until initialData is processed
  const pendingRequestRef = useRef(null);
  const isInitialLoadRef = useRef(true); // Tracks if the very first load (offline calc) happened
  const hasProcessedInitialDataRef = useRef(false); // New ref to track if initialData prop has been processed
  const hasCalculatedOfflineMiningRef = useRef(false); // Still useful for subsequent visibility changes

  // Process initialData when it's available and hasn't been processed yet
  useEffect(() => {
    if (initialData && initialData.success && !hasProcessedInitialDataRef.current) {
      console.log('[UserDataContext] Processing initialData from props:', initialData);
      // The backend initializeApp action returns:
      // { success: true, alreadyExisted, userData (mapped), miningData, dailyLoginData, sponsorsList, referralStats }

      // Combine data for the main userData state object
      // The core user attributes are in initialData.userData
      // Mining progress (minedAmount, lastMiningTime) is in initialData.miningData
      // Offline mining specific calculation details are also in initialData.miningData.offlineMining
      const combinedInitialState = {
        ...initialData.userData, // Contains balance, primary_coin_balance, username, referral_code etc.
        minedAmount: initialData.miningData?.minedAmount,
        lastMiningTime: initialData.miningData?.lastMiningTime,
        minerPower: initialData.miningData?.minerPower,
        offlineMining: initialData.miningData?.offlineMining, // For potential display
      };

      setUserData(combinedInitialState);
      // Also set sponsors and referralStats from initialData
      if (initialData.sponsorsList) {
        setSponsors(initialData.sponsorsList);
      }
      if (initialData.referralStats) {
        setReferralStats(initialData.referralStats);
      }
      // dailyLoginData can also be stored if a component needs it directly from context,
      // or components can use the getDailyLoginData function. For now, not storing it in a separate state.

      setIsLoading(false);
      hasProcessedInitialDataRef.current = true;
      isInitialLoadRef.current = false;
      hasCalculatedOfflineMiningRef.current = true;
      console.log('[UserDataContext] Initial data (including sponsors and referral stats) processed, isLoading set to false.');
    } else if (initialData && !initialData.success && !hasProcessedInitialDataRef.current) {
      console.error('[UserDataContext] initialData indicates failure:', initialData.error);
      setIsLoading(false);
      hasProcessedInitialDataRef.current = true;
    }
  }, [initialData]);


  // Track app visibility to determine when user returns (for refreshing mining progress)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInitialLoadRef.current) {
        // User returned to app after being away
        hasCalculatedOfflineMiningRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Direct data fetcher without caching
  const fetchUserData = useCallback(async (userId, forceRefresh = false, calculateOffline = false) => {
    if (!userId) return null;

    // If there's already a pending request for this user, wait for it
    if (pendingRequestRef.current && pendingRequestRef.current.userId === userId.toString()) {
      return pendingRequestRef.current.promise;
    }

    // Create new request
    setIsLoading(true);
    const requestPromise = (async () => {
      try {
        let offlineResult = null;
        
        // Only calculate offline mining on first load or when explicitly requested
        if (calculateOffline && !hasCalculatedOfflineMiningRef.current) {
          // Use server-side mining progress update to get current state
          offlineResult = await updateMiningProgress(userId);
          hasCalculatedOfflineMiningRef.current = true;
          
          // After first load, mark as no longer initial
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        }
        
        // Get fresh user data
        const userData = await getUserData(userId);
        
        if (userData) {
          // Combine the result - if we have offline mining data from server, use it
          const combinedData = {
            ...userData,
            offlineMining: offlineResult?.offlineMining || offlineResult
          };
          
          setUserData(combinedData);
          return combinedData;
        }
        
        return null;
      } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
      } finally {
        setIsLoading(false);
        pendingRequestRef.current = null;
      }
    })();

    // Store pending request
    pendingRequestRef.current = {
      userId: userId.toString(),
      promise: requestPromise
    };

    return requestPromise;
  }, []); // No dependencies - all state accessed via refs

  // Update mining progress using server-side calculations
  const updateServerMiningProgress = useCallback(async (userId) => {
    if (!userId) return null;
    
    try {
      const result = await updateMiningProgress(userId);
      
      if (result.success) {
        // Update local state with server-calculated mining data
        setUserData(prev => {
          if (prev) {
            return {
              ...prev,
              minedAmount: result.minedAmount,
              lastMiningTime: result.lastMiningTime,
              minerPower: result.minerPower
            };
          }
          return prev;
        });
        
        return result;
      }
      
      return result;
    } catch (error) {
      console.error('Error updating server mining progress:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Claim function with immediate state update
  const claimSecondaryCoin = useCallback(async (userId) => { // Remove minedAmount from params
    // userId here is expected to be the Appwrite User Document ID (userData.$id)
    if (!userId) {
      console.error('User Document ID is required for claimSecondaryCoin in context.');
      return { success: false, error: 'User ID is required.' };
    }

    // Note: The MINIMUM_CLAIM_AMOUNT check is now handled server-side by the Appwrite Function.
    // The client can still show a disabled button based on its view of `minedAmount`,
    // but the authoritative check is server-side.

    try {
      // Call the updated service function which no longer takes minedAmount
      const result = await claimMinedSecondaryCoin(userId);
      
      if (result.success) {
        // Immediately update state based on server's response
        setUserData(prev => {
          if (prev) {
            return {
              ...prev,
              balance: result.newBalance, // This is secondary_coin_balance
              minedAmount: 0, // Reset local minedAmount as server has processed it
              // lastClaimTime will be updated on the server; fetchUserData will get it if needed
              // For optimistic UI, if the server function returned lastClaimTime, we could set it here.
              // Assuming the server function doesn't explicitly return it now,
              // so we rely on next fetch or keep the old one until refresh.
            };
          }
          return prev;
        });
      }
      
      return result; // result contains { success, claimedAmount, newBalance } from server
    } catch (error) {
      console.error(`Error claiming ${APP_CONFIG.SECONDARY_COIN_NAME} in context:`, error);
      return { success: false, error: error.message || `Failed to process claim for ${APP_CONFIG.SECONDARY_COIN_NAME}.` };
    }
  }, []); // Dependencies: APP_CONFIG.SECONDARY_COIN_NAME (if used in error message directly)

  // Force refresh function
  const refreshUserData = useCallback(async (userId) => {
    return fetchUserData(userId, true, false); // No offline calculation on manual refresh
  }, [fetchUserData]);

  // Initial data load with offline mining calculation
  const initialLoadUserData = useCallback(async (userId) => {
    // Prevent duplicate initial loads for the same user
    if (pendingRequestRef.current?.userId === userId.toString()) {
      return pendingRequestRef.current.promise;
    }
    
    return fetchUserData(userId, false, true);
  }, [fetchUserData]);

  // Standard getUserData function
  const getOptimizedUserData = useCallback(async (userId) => {
    return fetchUserData(userId, false, false);
  }, [fetchUserData]);

  // Update specific fields in state
  const updateCachedField = useCallback((userId, field, value) => {
    setUserData(prev => {
      if (prev) {
        return {
          ...prev,
          [field]: value
        };
      }
      return prev;
    });
  }, []);

  // Get daily login information
  const getDailyLoginData = useCallback(async (userId) => {
    if (!userId) return null;
    
    try {
      return await getDailyLoginInfo(userId);
    } catch (error) {
      console.error('Error fetching daily login data:', error);
      return null;
    }
  }, []);
  
  // Claim daily login reward
  const claimDailyReward = useCallback(async (userId) => {
    if (!userId) return { success: false, error: 'User ID is required' };
    
    try {
      const result = await claimDailyLoginReward(userId);
      
      if (result.success) {
        // Update local userData state with new primary coin balance
        setUserData(prev => {
          if (prev) {
            return {
              ...prev,
              primary_coin_balance: result.newBalance
            };
          }
          return prev;
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error claiming daily reward:', error);
      return { success: false, error: error.message || 'Failed to claim daily reward' };
    }
  }, []);

  // Claim sponsor task reward
  const claimSponsorTask = useCallback(async (userId, sponsorId) => {
    if (!userId || !sponsorId) {
      throw new Error('User and Sponsor IDs are required.');
    }

    try {
      const result = await claimSponsorReward(userId, sponsorId);

      if (result.success) {
        // Optimistically update the user's state for a fast UI response
        setUserData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            primary_coin_balance: result.newBalance,
            claimed_sponsor_rewards: [...(prev.claimed_sponsor_rewards || []), result.claimedSponsorId],
          };
        });
      }

      return result; // Forward the full result to the UI component
    } catch (error) {
      console.error('Error claiming sponsor reward in context:', error);
      throw error; // Rethrow for the UI component to handle
    }
  }, []);

  const value = useMemo(() => ({
    userData,
    sponsors, // Expose sponsors
    referralStats, // Expose referralStats
    isLoading,
    fetchUserData,
    getOptimizedUserData,
    initialLoadUserData,
    claimSecondaryCoin,
    refreshUserData,
    getCachedUserData: () => userData,
    updateCachedField,
    // isCacheValid: () => false, // Removed as it's unused
    getDailyLoginData,
    claimDailyReward,
    claimSponsorTask,
    updateServerMiningProgress
  }), [
    userData,
    sponsors, // Add to dependency array
    referralStats, // Add to dependency array
    isLoading,
    fetchUserData,
    getOptimizedUserData,
    initialLoadUserData,
    claimSecondaryCoin,
    refreshUserData,
    updateCachedField,
    getDailyLoginData,
    claimDailyReward,
    claimSponsorTask,
    updateServerMiningProgress
  ]);

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

export default UserDataContext;
